import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Stack, CircularProgress, Alert, Grid, Card,
  CardContent, CardActions, Avatar, Chip, Button, IconButton, Tooltip,
  alpha, Divider, LinearProgress, TextField, InputAdornment,
  MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { fetchApprovedSubscriptions } from '../designApi';

// ✅ Safe array extraction
const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.subscriptions)) return data.subscriptions;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

export default function DesignManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchApprovedSubscriptions();
      // ✅ Backend getApprovedSubscriptions returns direct array
      // Each item has mapSubscriptionResponse flat fields PLUS nested Admin, Plan objects
      const subsArray = extractArray(data);
      setSubscriptions(subsArray);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load subscriptions');
      setSubscriptions([]); // ✅ Reset on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const getProgress = (sub) => {
    const received = sub.UploadedDesignsCount || 0;
    const sent = sub.SentDesignsCount || 0;
    const total = sub.NumberOfDesigns || 1;
    return {
      received,
      sent,
      total,
      receivedPercent: Math.min((received / total) * 100, 100),
      sentPercent: Math.min((sent / total) * 100, 100),
      isComplete: sub.Status === 'completed' || (sent >= total)
    };
  };

  // ✅ Safe array guard
  const safeSubs = Array.isArray(subscriptions) ? subscriptions : [];

  const filteredSubscriptions = safeSubs.filter(sub => {
    // ✅ Backend returns nested Admin object: sub.Admin?.Name
    // AND flat PlanName from mapSubscriptionResponse
    const matchesSearch = !searchQuery ||
      sub.SubscriptionId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.PlanName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.Admin?.Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.Admin?.Number?.toLowerCase().includes(searchQuery.toLowerCase());

    const progress = getProgress(sub);
    let matchesStatus = true;
    if (statusFilter === 'waiting') matchesStatus = progress.received === 0;
    else if (statusFilter === 'inProgress') matchesStatus = progress.received > 0 && !progress.isComplete;
    else if (statusFilter === 'completed') matchesStatus = progress.isComplete;

    return matchesSearch && matchesStatus;
  });

  const totalActive = safeSubs.filter(s => !getProgress(s).isComplete).length;
  const totalCompleted = safeSubs.filter(s => getProgress(s).isComplete).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#6a1b9a', 0.08)} 0%, ${alpha('#ad1457', 0.08)} 100%)`,
          border: `1px solid ${alpha('#6a1b9a', 0.15)}`
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#6a1b9a', width: 50, height: 50 }}>
              <DesignServicesIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#6a1b9a">
                {t('DesignManagement') || 'Design Management'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('DesignMgmtDesc') || 'Manage received images and send back designed images'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`${totalActive} ${t('Active') || 'Active'}`} color="warning" sx={{ fontWeight: 600 }} />
            <Chip label={`${totalCompleted} ${t('Completed') || 'Completed'}`} color="success" sx={{ fontWeight: 600 }} />
            <Tooltip title={t('Refresh') || 'Refresh'}>
              <IconButton onClick={loadSubscriptions} sx={{ bgcolor: alpha('#6a1b9a', 0.1) }}>
                <RefreshIcon sx={{ color: '#6a1b9a' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Search & Filter */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: `1px solid ${alpha('#000', 0.08)}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder={t('SearchSubscriptions') || 'Search by ID, plan, admin...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('Filter') || 'Filter'}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label={t('Filter') || 'Filter'}
            >
              <MenuItem value="all">{t('All') || 'All'}</MenuItem>
              <MenuItem value="waiting">{t('WaitingForImages') || 'Waiting for Images'}</MenuItem>
              <MenuItem value="inProgress">{t('InProgress') || 'In Progress'}</MenuItem>
              <MenuItem value="completed">{t('Completed') || 'Completed'}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Subscription Cards */}
      {filteredSubscriptions.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha('#000', 0.2)}` }}>
          <DesignServicesIcon sx={{ fontSize: 64, color: alpha('#000', 0.15), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('NoApprovedSubscriptions') || 'No approved subscriptions found'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredSubscriptions.map((sub, index) => {
            const progress = getProgress(sub);
            // ✅ Backend returns nested Admin: { Id, Name, Number, BranchId }
            const adminName = sub.Admin?.Name || `Admin #${sub.AdminId || '?'}`;
            const adminNumber = sub.Admin?.Number || '';

            return (
              <Grid item xs={12} sm={6} lg={4} key={sub.Id || index}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(progress.isComplete ? '#2e7d32' : '#6a1b9a', 0.2)}`,
                    transition: 'all 0.3s',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      boxShadow: `0 4px 20px ${alpha('#6a1b9a', 0.15)}`,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  {/* Completion badge */}
                  {progress.isComplete && (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      label={t('Completed') || 'Completed'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: 12,
                        bgcolor: '#2e7d32',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 3 }}>
                    {/* Subscription ID */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Chip
                        label={sub.SubscriptionId || '-'}
                        size="small"
                        sx={{ bgcolor: alpha('#6a1b9a', 0.1), color: '#6a1b9a', fontWeight: 700, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          const dateStr = sub.ApprovedAt || sub.CreatedAt;
                          if (!dateStr) return '-';
                          try { return new Date(dateStr).toLocaleDateString(); }
                          catch { return '-'; }
                        })()}
                      </Typography>
                    </Stack>

                    {/* Admin Info - ✅ Uses nested Admin object from backend */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#ad1457', 0.1), width: 40, height: 40, fontWeight: 700 }}>
                        {adminName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {adminName}
                        </Typography>
                        {adminNumber && (
                          <Typography variant="caption" color="text.secondary">
                            {adminNumber}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {/* Plan Info - ✅ PlanName is flat field from mapSubscriptionResponse */}
                    <Paper sx={{ p: 1.5, bgcolor: alpha('#000', 0.02), borderRadius: 2, mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DesignServicesIcon sx={{ fontSize: 18, color: '#ad1457' }} />
                          <Typography variant="body2" fontWeight={600}>{sub.PlanName || '-'}</Typography>
                        </Stack>
                        <Chip
                          label={`${progress.total} designs`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', bgcolor: alpha('#9c27b0', 0.1), color: '#9c27b0' }}
                        />
                      </Stack>
                    </Paper>

                    <Divider sx={{ mb: 2 }} />

                    {/* Received Images Progress */}
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <DownloadIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                          <Typography variant="caption" fontWeight={600} color="#1565c0">
                            {t('ReceivedImages') || 'Received Images'}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" fontWeight={700} color="#1565c0">
                          {progress.received} / {progress.total}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={progress.receivedPercent}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha('#1565c0', 0.1),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: '#1565c0'
                          }
                        }}
                      />
                    </Box>

                    {/* Sent Images Progress */}
                    <Box sx={{ mb: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <SendIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                          <Typography variant="caption" fontWeight={600} color="#2e7d32">
                            {t('SentDesigns') || 'Sent Designs'}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" fontWeight={700} color="#2e7d32">
                          {progress.sent} / {progress.total}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={progress.sentPercent}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha('#2e7d32', 0.1),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: '#2e7d32'
                          }
                        }}
                      />
                    </Box>

                    {/* Status indicator */}
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      {progress.received === 0 ? (
                        <Chip
                          icon={<HourglassTopIcon sx={{ fontSize: 16 }} />}
                          label={t('WaitingForImages') || 'Waiting for images'}
                          size="small"
                          sx={{ bgcolor: alpha('#ed6c02', 0.1), color: '#ed6c02', fontWeight: 600 }}
                        />
                      ) : progress.isComplete ? (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                          label={t('AllDesignsSent') || 'All designs sent'}
                          size="small"
                          sx={{ bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32', fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          icon={<DesignServicesIcon sx={{ fontSize: 16 }} />}
                          label={t('InProgress') || 'In progress'}
                          size="small"
                          sx={{ bgcolor: alpha('#6a1b9a', 0.1), color: '#6a1b9a', fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ px: 3, pb: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate(`/admin/design-management/${sub.Id}`)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 600,
                        bgcolor: progress.isComplete ? '#2e7d32' : '#6a1b9a',
                        '&:hover': {
                          bgcolor: progress.isComplete ? '#1b5e20' : '#4a148c'
                        }
                      }}
                    >
                      {progress.isComplete
                        ? (t('ViewDetails') || 'View Details')
                        : (t('ManageDesigns') || 'Manage Designs')
                      }
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}